import React, { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import Plus from 'lucide-react/dist/esm/icons/plus';
import Eye from 'lucide-react/dist/esm/icons/eye';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import Pencil from 'lucide-react/dist/esm/icons/pencil';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import MoreVertical from 'lucide-react/dist/esm/icons/more-vertical';
import { PageSizeSelect, SortableHeader, TableEmptyRow, formatDate } from '../shared';
import { useAmlStore } from '../../stores/amlStore';
import {
  SearchInput, Button, IconButton, Spinner, Badge, DropdownMenu,
} from '../../../../components/ui';
import Pagination from '../../../../components/common/Pagination';
import { usePermissions, Permissions } from '../../../../hooks/usePermissions';
import { useUIStore } from '../../../../stores/uiStore';
import { getTranslation } from '../../../../i18n';
import type { AmlCustomer } from '../../types/aml';

const DEBOUNCE_MS = 300;

const SCREENING_MAP = {
  cleared: { variant: 'success', labelKey: 'amlScreeningResultCleared' },
  potential_match: { variant: 'danger', labelKey: 'amlScreeningResultPotentialMatch' },
} as const;

export type CustomerAction = 'detail' | 'kyc' | 'edit' | 'delete';

interface CustomerListProps {
  onAction?: (action: CustomerAction, customer: AmlCustomer) => void;
}

interface CustomerRowProps {
  customer: AmlCustomer;
  canManage: boolean;
  onAction?: (action: CustomerAction, customer: AmlCustomer) => void;
  t: (key: string) => string;
}

const CustomerRow = React.memo<CustomerRowProps>(({ customer, canManage, onAction, t }) => {
  const menuItems = useMemo(() => [
    {
      label: t('amlCustomerDropdownDetail'),
      icon: <Eye size={16} />,
      onClick: () => onAction?.('detail', customer),
    },
    {
      label: t('amlCustomerDropdownKycReport'),
      icon: <FileText size={16} />,
      onClick: () => onAction?.('kyc', customer),
    },
    {
      label: t('amlCustomerDropdownEdit'),
      icon: <Pencil size={16} />,
      onClick: () => onAction?.('edit', customer),
      disabled: !canManage,
    },
    {
      label: t('amlCustomerDropdownDelete'),
      icon: <Trash2 size={16} />,
      onClick: () => onAction?.('delete', customer),
      danger: true,
      disabled: !canManage,
    },
  ], [customer, canManage, onAction, t]);

  const screeningMeta = customer.screening_status
    ? SCREENING_MAP[customer.screening_status]
    : null;

  return (
    <tr className="border-b border-subtle transition-colors hover:bg-surface-secondary/50 bg-surface-primary">
      <td className="px-4 py-3 text-sm font-medium text-content-primary whitespace-nowrap">{customer.name}</td>
      <td className="px-4 py-3 text-sm text-content-secondary whitespace-nowrap">{formatDate(customer.initial_date)}</td>
      <td className="px-4 py-3 text-sm text-content-secondary">{customer.code}</td>
      <td className="px-4 py-3 text-sm text-content-secondary whitespace-nowrap">{customer.registration_number || t('amlCustomerNotApplicable')}</td>
      <td className="px-4 py-3 whitespace-nowrap">
        {screeningMeta ? (
          <Badge variant={screeningMeta.variant} size="sm" dot>
            {t(screeningMeta.labelKey)}
          </Badge>
        ) : (
          <Badge variant="neutral" size="sm">
            {t('amlScreeningResultNotScreened')}
          </Badge>
        )}
      </td>
      <td className="px-4 py-3">
        <Badge variant={customer.status ? 'success' : 'neutral'} size="sm">
          {customer.status ? t('amlCustomerStatusActive') : t('amlCustomerStatusInactive')}
        </Badge>
      </td>
      <td className="px-4 py-3 text-right">
        <DropdownMenu
          align="right"
          trigger={
            <IconButton
              icon={<MoreVertical size={16} />}
              variant="ghost"
              aria-label={t('amlCustomerAriaLabelActions')}
            />
          }
          items={menuItems}
        />
      </td>
    </tr>
  );
});

CustomerRow.displayName = 'CustomerRow';

const CustomerList: React.FC<CustomerListProps> = ({ onAction }) => {
  const {
    customers,
    customersTotal,
    customersPage,
    customersSearch,
    customersPageSize,
    customersSort,
    customersSortDir,
    loading,
    customersLoading,
    fetchCustomers,
    setCustomersSearch,
    setCustomersPageSize,
    setCustomersSort,
    setShowForm,
  } = useAmlStore();

  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permissions.AML_CUSTOMERS_MANAGE);
  const { language } = useUIStore();
  const t = useCallback((key: string) => getTranslation(language, key), [language]);

  const [localSearch, setLocalSearch] = useState(customersSearch);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(false);

  const totalPages = Math.max(1, Math.ceil(customersTotal / customersPageSize));
  const startItem = (customersPage - 1) * customersPageSize + 1;
  const endItem = Math.min(customersPage * customersPageSize, customersTotal);

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setLocalSearch(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setCustomersSearch(value);
        debounceRef.current = null;
      }, DEBOUNCE_MS);
    },
    [setCustomersSearch],
  );

  const handleAddCustomer = useCallback(
    () => setShowForm('customer-create'),
    [setShowForm],
  );

  useEffect(() => {
    setLocalSearch(customersSearch);
  }, [customersSearch]);

  useEffect(() => {
    fetchCustomers(1);
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!mountedRef.current) return;
    fetchCustomers(1);
  }, [customersSearch, customersSort, customersSortDir, customersPageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <SearchInput
          value={localSearch}
          onChange={handleSearchInputChange}
          placeholder={t('amlCustomerSearchPlaceholder')}
          className="flex-1 max-w-sm"
        />
        {canManage && (
          <Button variant="success" size="lg" leftIcon={<Plus size={16} />} onClick={handleAddCustomer}>
            {t('amlBtnAddCustomer')}
          </Button>
        )}
      </div>

      <div className="rounded-xl border border-subtle bg-surface-primary overflow-hidden">
        {loading && customers.length === 0 ? (
          <div className="flex items-center justify-center h-48 text-content-muted">
            <Spinner size="lg" />
          </div>
        ) : !loading && customers.length === 0 ? (
          <TableEmptyRow title={t('amlTableNoCustomersFound')} />
        ) : (
          <>
            <div
              className={`overflow-x-auto${customersLoading ? ' opacity-60 pointer-events-none' : ''}`}
              style={{ transition: 'opacity 0.15s ease' }}
            >
              <table className="w-full min-w-[840px]">
                <thead>
                  <tr className="border-b border-subtle bg-surface-secondary/50">
                    <SortableHeader label={t('amlColCustomerLegalName')} field="name" currentSort={customersSort} currentDir={customersSortDir} onSort={setCustomersSort} />
                    <SortableHeader label={t('amlColInitialDate')} field="initial_date" currentSort={customersSort} currentDir={customersSortDir} onSort={setCustomersSort} />
                    <SortableHeader label={t('amlColCode')} field="code" currentSort={customersSort} currentDir={customersSortDir} onSort={setCustomersSort} />
                    <th className="px-4 py-3 text-left text-xs font-semibold text-content-muted uppercase tracking-wider whitespace-nowrap">{t('amlColRegistrationNumber')}</th>
                    <SortableHeader label={t('amlColScreening')} field="screening_status" currentSort={customersSort} currentDir={customersSortDir} onSort={setCustomersSort} />
                    <SortableHeader label={t('amlColStatus')} field="status" currentSort={customersSort} currentDir={customersSortDir} onSort={setCustomersSort} />
                    <th className="px-4 py-3 w-[50px]" />
                  </tr>
                </thead>
                <tbody>
                  {customers.map((c) => (
                    <CustomerRow
                      key={c.id}
                      customer={c}
                      canManage={canManage}
                      onAction={onAction}
                      t={t}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {customersTotal > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t border-subtle bg-surface-secondary/30">
                <PageSizeSelect value={customersPageSize} onChange={setCustomersPageSize} />
                <Pagination
                  page={customersPage}
                  totalPages={totalPages}
                  onPageChange={fetchCustomers}
                  totalItems={customersTotal}
                  startIndex={startItem - 1}
                  endIndex={endItem}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerList;
